/**
 * Sticky Add To Cart
 * ------------------
 * Keeps the sticky bar's dropdown variant selectors in sync with the main
 * product's variant picker in both directions, relabels sold out option
 * values live, and forwards "Add to cart" clicks to the real product form
 * so the existing cart-drawer / notification / error handling keeps working.
 *
 * IMPORTANT: the theme replaces the main product's `<variant-selects>` (and
 * sometimes the whole `<product-info>`) with a brand new DOM node every time
 * a variant changes (assets/global.js `HTMLUpdateUtility.viewTransition`).
 * Because of that, this file never caches a reference to those nodes -
 * only the stable `section.id` string is cached, and the live node is
 * re-queried from the DOM every single time it's needed. Caching the node
 * itself is what caused sync to break after the first change.
 */
(function () {
  if (customElements.get('sticky-atc')) return;

  const MODAL_SELECTOR = 'quick-add-modal, product-modal, dialog, [class*="modal" i]';
  const SOLD_OUT_SUFFIX_RE = /\s*-\s*[^-]+$/;

  class StickyAddToCart extends HTMLElement {
    connectedCallback() {
      this.productId = this.dataset.productId;
      this.selects = Array.from(this.querySelectorAll('.sticky-atc__select'));
      this.submitButton = this.querySelector('.sticky-atc__submit');
      this.submitText = this.querySelector('.sticky-atc__submit-text');
      this.priceEl = this.querySelector('.sticky-atc__price');
      this.imageEl = this.querySelector('.sticky-atc__image');

      const variantsScript = this.querySelector('[data-sticky-variants-json]');
      try {
        this.variants = variantsScript ? JSON.parse(variantsScript.textContent) : [];
      } catch (error) {
        this.variants = [];
      }

      this.isSyncingFromMain = false;
      this.pubsubUnsubscribers = [];

      // One-time discovery; only the section id STRING is kept, never a node.
      this.mainSectionId = this.findMainSectionId();

      this.bindStickyControls();
      this.bindMainProductEvents();
      this.initAutoVisibility();
      this.recomputeOptionAvailability();
      this.applyOptimisticAvailability();
    }

    disconnectedCallback() {
      this.pubsubUnsubscribers.forEach((unsubscribe) => unsubscribe());
      this.visibilityObserver?.disconnect();
    }

    /* ------------------------------------------------------------------ */
    /* Locating the main product (always via live DOM lookups)             */
    /* ------------------------------------------------------------------ */

    findMainSectionId() {
      const candidates = Array.from(document.querySelectorAll('product-info')).filter(
        (el) => !el.closest(MODAL_SELECTOR)
      );
      const match = candidates.find((el) => el.dataset.productId === this.productId) || candidates[0];
      return match ? match.dataset.section : null;
    }

    // Always re-queried: the node backing this section id gets replaced by
    // the theme on every variant change, so a cached reference goes stale.
    getMainVariantSelects() {
      if (!this.mainSectionId) return null;
      return document.querySelector(`variant-selects[data-section="${this.mainSectionId}"]`);
    }

    getMainSubmitButton() {
      if (!this.mainSectionId) return null;
      return document.getElementById(`ProductSubmitButton-${this.mainSectionId}`);
    }

    /* ------------------------------------------------------------------ */
    /* Sticky bar -> main product                                          */
    /* ------------------------------------------------------------------ */

    bindStickyControls() {
      this.selects.forEach((select) => {
        select.addEventListener('change', () => {
          if (this.isSyncingFromMain) return;
          this.pushSelectionToMain(select);
          this.recomputeOptionAvailability();
          this.applyOptimisticAvailability();
        });
      });

      this.submitButton?.addEventListener('click', () => this.handleAddToCart());
    }

    pushSelectionToMain(select) {
      const mainVariantSelects = this.getMainVariantSelects();
      if (!mainVariantSelects) return;

      const chosenOption = select.selectedOptions[0];
      const valueId = chosenOption?.dataset.optionValueId;
      if (!valueId) return;

      const target = mainVariantSelects.querySelector(`[data-option-value-id="${valueId}"]`);
      if (!target) return;

      if (target.tagName === 'OPTION') {
        const parentSelect = target.closest('select');
        if (!parentSelect || parentSelect.value === target.value) return;
        parentSelect.value = target.value;
        parentSelect.dispatchEvent(new Event('change', { bubbles: true }));
      } else if (target.tagName === 'INPUT' && !target.checked) {
        target.checked = true;
        target.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    /* ------------------------------------------------------------------ */
    /* Main product -> sticky bar                                          */
    /* ------------------------------------------------------------------ */

    bindMainProductEvents() {
      if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

      this.pubsubUnsubscribers.push(
        subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, ({ data }) => {
          const sourceVariantSelects = data?.event?.target?.closest('variant-selects');
          if (!sourceVariantSelects || sourceVariantSelects.dataset.productId !== this.productId) return;
          if (sourceVariantSelects.closest(MODAL_SELECTOR)) return;

          // Self-heal in case initial discovery ran before the main
          // section existed, or the section id ever changes.
          this.mainSectionId = sourceVariantSelects.dataset.section;

          this.pullSelectionFromMain(sourceVariantSelects);
          this.recomputeOptionAvailability();
          this.applyOptimisticAvailability();
        })
      );

      this.pubsubUnsubscribers.push(
        subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          if (!this.mainSectionId || data?.sectionId !== this.mainSectionId) return;
          this.applyVariantData(data.variant, data.html);
        })
      );
    }

    pullSelectionFromMain(sourceVariantSelects) {
      const scope = sourceVariantSelects || this.getMainVariantSelects();
      if (!scope || !this.selects.length) return;

      const selectedEls = Array.from(
        scope.querySelectorAll('select option[selected], fieldset input:checked')
      );

      this.isSyncingFromMain = true;
      selectedEls.forEach((el) => {
        const valueId = el.dataset.optionValueId;
        if (!valueId) return;
        const stickyOption = this.querySelector(`.sticky-atc__select option[data-option-value-id="${valueId}"]`);
        if (!stickyOption) return;
        const stickySelect = stickyOption.closest('select');
        if (stickySelect && stickySelect.value !== stickyOption.value) {
          stickySelect.value = stickyOption.value;
        }
      });
      this.isSyncingFromMain = false;
    }

    applyVariantData(variant, html) {
      if (this.priceEl && html && this.mainSectionId) {
        const sourcePrice = html.getElementById(`price-${this.mainSectionId}`);
        if (sourcePrice) this.priceEl.innerHTML = sourcePrice.innerHTML;
      }

      if (this.imageEl && variant?.featured_image?.src) {
        const src = variant.featured_image.src;
        this.imageEl.src = src.includes('?') ? `${src}&width=120` : `${src}?width=120`;
      }

      if (!variant) {
        this.setButtonState('unavailable');
        return;
      }

      this.setButtonState(variant.available ? 'available' : 'sold-out');
    }

    setButtonState(state) {
      if (!this.submitButton) return;

      const isSoldOut = state !== 'available';
      this.submitButton.classList.toggle('is-sold-out', isSoldOut);
      this.submitButton.disabled = isSoldOut;

      if (this.submitText) {
        if (state === 'unavailable') {
          this.submitText.textContent = this.dataset.unavailableText;
        } else if (state === 'sold-out') {
          this.submitText.textContent = this.dataset.soldOutText;
        } else {
          this.submitText.textContent = this.dataset.addToCartText;
        }
      }
    }

    /* ------------------------------------------------------------------ */
    /* Instant (pre-fetch) sold-out feedback                               */
    /* ------------------------------------------------------------------ */

    // `this.selects` is built in the same left-to-right option order as
    // `variant.options`, so comparing by index (rather than option name)
    // is enough to find the exact matching variant locally, with no
    // network round trip - this is what makes the button text update the
    // instant a sold out combination is selected, instead of waiting on
    // the section fetch to resolve.
    getLocallyMatchedVariant() {
      if (!this.variants.length || !this.selects.length) return null;
      const currentSelection = this.selects.map((select) => select.value);
      return (
        this.variants.find((variant) =>
          currentSelection.every((value, index) => variant.options[index] === value)
        ) || null
      );
    }

    applyOptimisticAvailability() {
      if (!this.selects.length) return;
      const variant = this.getLocallyMatchedVariant();
      if (!variant) {
        this.setButtonState('unavailable');
        return;
      }
      this.setButtonState(variant.available ? 'available' : 'sold-out');
    }

    /* ------------------------------------------------------------------ */
    /* Live sold-out relabeling                                            */
    /* ------------------------------------------------------------------ */

    recomputeOptionAvailability() {
      if (!this.variants.length || !this.selects.length) return;

      const currentSelection = this.selects.map((select) => ({
        position: parseInt(select.dataset.optionPosition, 10),
        value: select.value,
      }));

      this.selects.forEach((select) => {
        const position = parseInt(select.dataset.optionPosition, 10);

        Array.from(select.options).forEach((option) => {
          const baseLabel = option.dataset.baseLabel || option.textContent.replace(SOLD_OUT_SUFFIX_RE, '');
          option.dataset.baseLabel = baseLabel;

          const isAvailable = this.variants.some((variant) => {
            if (!variant.available) return false;
            if (variant.options[position - 1] !== option.value) return false;
            return currentSelection.every(
              (selection) =>
                selection.position === position || variant.options[selection.position - 1] === selection.value
            );
          });

          option.textContent = isAvailable ? baseLabel : `${baseLabel} - ${this.dataset.soldOutText}`;
          option.classList.toggle('is-sold-out-option', !isAvailable);
        });
      });
    }

    /* ------------------------------------------------------------------ */
    /* Add to cart                                                         */
    /* ------------------------------------------------------------------ */

    handleAddToCart() {
      const mainSubmitButton = this.getMainSubmitButton();
      if (!mainSubmitButton) return;
      if (mainSubmitButton.disabled || mainSubmitButton.getAttribute('aria-disabled') === 'true') return;

      this.submitButton.classList.add('is-loading');

      let stopped = false;
      const stopLoading = () => {
        if (stopped) return;
        stopped = true;
        this.submitButton.classList.remove('is-loading');
        cartUpdateUnsub?.();
        cartErrorUnsub?.();
      };

      let cartUpdateUnsub;
      let cartErrorUnsub;
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        cartUpdateUnsub = subscribe(PUB_SUB_EVENTS.cartUpdate, stopLoading);
        cartErrorUnsub = subscribe(PUB_SUB_EVENTS.cartError, stopLoading);
      }
      window.setTimeout(stopLoading, 6000);

      mainSubmitButton.click();
    }

    /* ------------------------------------------------------------------ */
    /* Auto show / hide on scroll                                          */
    /* ------------------------------------------------------------------ */

    initAutoVisibility() {
      const mainSubmitButton = this.getMainSubmitButton();
      if (this.dataset.autoShow !== 'true' || !mainSubmitButton || !('IntersectionObserver' in window)) {
        this.classList.add('is-visible');
        return;
      }

      this.visibilityObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => this.classList.toggle('is-visible', !entry.isIntersecting));
        },
        { threshold: 0 }
      );
      this.visibilityObserver.observe(mainSubmitButton);
    }
  }

  customElements.define('sticky-atc', StickyAddToCart);
})();
